import React, { useEffect, useState } from 'react';
import { api, msg, reason, pct, money, num, time } from './hubApi';
import { useApp } from './HubContext';

const STATUS = { PAPER: 'Paper', MATCHED: 'Matched', PARTIAL: 'Part matched', UNMATCHED: 'Unmatched', QUEUED: 'Queued', ALERT_PENDING: 'Alert',
  SKIPPED: 'Skipped', FAILED: 'Failed', SETTLED: 'Settled', RECONCILE: 'Checking', SUBMITTING: 'Placing' };

// Leaderboard fairness: units per bet come from a fixed ladder (matches the server's clampUnits).
export const UNIT_LADDER = [0.5, 1, 1.5, 2, 2.5, 3, 4, 5];
const ul = u => (u === 0.5 ? '½' : String(u)) + 'u';
function UnitPicker({ value, onChange, unitValue }) {
  return (
    <div className="unitpick" role="group" aria-label="Units for this bet">
      <button type="button" className={value == null ? 'on' : ''} onClick={() => onChange(null)} title="Use the units written in the tip, or 1u">As tipped</button>
      {UNIT_LADDER.map(u => <button type="button" key={u} className={value === u ? 'on' : ''} onClick={() => onChange(u)} title={'A$' + (u * (unitValue || 10)).toFixed(2)}>{ul(u)}</button>)}
    </div>
  );
}

function UnitBar() {
  const { user, setUser } = useApp();
  const [v, setV] = useState(user.unitValue || 10);
  const [note, setNote] = useState('');
  const save = async () => { try { setUser(await api('/me', { method: 'PATCH', body: { unitValue: Number(v) } })); setNote('Saved'); } catch (e) { setNote(msg(e)); } };
  return (
    <section className="card unitbar">
      <div className="row between">
        <div><h2>Your unit: 1u = A${Number(user.unitValue || 10).toFixed(2)}</h2>
          <p className="fine">Everything you bet is recorded in units so your record compares fairly with every punter. When you mimic a leader, each unit they bet is placed as 1 × your unit in your own Betfair account.</p>
          {user.leaderboard?.optIn && <p className="fine"><b>Leaderboard rules:</b> you choose ½u to 5u on every bet. Your record counts those units, so changing the dollar value of your unit changes what you stake, never your record.</p>}</div>
        <div className="row"><label>1 unit = A$<input type="number" min="1" step="1" value={v} onChange={e => setV(e.target.value)} style={{ width: 100 }} /></label><button className="sm" onClick={save}>Save</button><span className="note">{note}</span></div>
      </div>
    </section>
  );
}

function Inbox({ sources, onDone }) {
  const { user } = useApp();
  const [units, setUnits] = useState(null);
  const [sourceId, setSourceId] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  useEffect(() => { if (!sourceId && sources.length) setSourceId(sources[0]._id); }, [sources]);
  const send = async body => {
    setBusy(true); setNote('');
    try {
      const r = await api('/tips', { method: 'POST', body: { sourceId, ...body, ...(units != null ? { units } : {}) } });
      const placed = r.filter(x => x.intent && !['SKIPPED', 'FAILED'].includes(x.intent.status)).length;
      const held = r.filter(x => x.tip.status === 'HELD').length;
      const skipped = r.filter(x => x.intent?.status === 'SKIPPED').length;
      setNote(r.length ? `${r.length} tip${r.length > 1 ? 's' : ''} read · ${placed} actioned · ${held} need a check · ${skipped} skipped by your rules` : 'No tips found in that text.');
      setText(''); onDone();
    } catch (e) { setNote(msg(e)); }
    setBusy(false);
  };
  const onFile = async e => {
    const file = e.target.files[0]; if (!file) return;
    if (file.name.endsWith('.csv')) return send({ csv: await file.text() });
    const base64 = await new Promise(res => { const fr = new FileReader(); fr.onload = () => res(String(fr.result).split(',')[1]); fr.readAsDataURL(file); });
    send({ screenshot: { base64, mediaType: file.type || 'image/png' } });
    e.target.value = '';
  };
  const manual = sources.filter(s => s.kind === 'MANUAL');
  return (
    <section className="card">
      <h2>Tip inbox</h2>
      <div className="row">
        <label className="grow">Source
          <select value={sourceId} onChange={e => setSourceId(e.target.value)}>
            {manual.map(s => <option key={s._id} value={s._id}>{s.name} · {s.mode}{s.provenance === 'CURATED' ? ' · curated' : ''}</option>)}
          </select>
        </label>
      </div>
      <textarea rows={5} value={text} onChange={e => setText(e.target.value)}
        placeholder={'Paste tips, one per line, e.g.\nRandwick R4 #7 Storm Warning win @ 4.5+ 2u\nFlemington R3 5 e/w\nEagle Farm 2-6 place'} />
      <div className="row unitrow"><span className="note">Units per bet</span><UnitPicker value={units} onChange={setUnits} unitValue={user?.unitValue} />
        <span className="note">{units != null ? `${ul(units)} = A$${(units * (user?.unitValue || 10)).toFixed(2)} on each tip` : 'Uses the units written in each tip, or 1u'}</span></div>
      <div className="row">
        <button disabled={busy || !text.trim()} onClick={() => send({ text })}>{busy ? 'Reading…' : 'Send tips'}</button>
        <label className="filebtn">Screenshot or CSV<input type="file" accept="image/*,.csv" onChange={onFile} hidden /></label>
        {note && <span className="note">{note}</span>}
      </div>
    </section>
  );
}

function Held({ held, onDone }) {
  const [markets, setMarkets] = useState([]);
  const [pick, setPick] = useState({});
  const [hu, setHu] = useState({});
  useEffect(() => { if (held.length) api('/markets').then(setMarkets).catch(() => {}); }, [held.length]);
  if (!held.length) return null;
  const confirm = async t => {
    const [marketId, selectionId] = (pick[t._id] || '').split('|');
    try { await api(`/tips/${t._id}/confirm`, { method: 'POST', body: { marketId, selectionId, ...(hu[t._id] ? { units: hu[t._id] } : {}) } }); onDone(); } catch (e) { alertMsg(e); }
  };
  const reject = async t => { await api(`/tips/${t._id}/reject`, { method: 'POST' }); onDone(); };
  const alertMsg = e => setPick(p => ({ ...p, _err: msg(e) }));
  return (
    <section className="card warn">
      <h2>Needs a check ({held.length})</h2>
      <p className="fine">We never guess. These tips were unclear, so nothing was placed. Pick the right runner or dismiss.</p>
      {pick._err && <p className="error">{pick._err}</p>}
      <div className="scroll"><table><tbody>
        {held.map(t => {
          const type = t.candidate.betType === 'PLACE' ? 'PLACE' : 'WIN';
          const opts = markets.filter(m => m.marketType === type && (!t.candidate.venueText || m.venue === t.candidate.venueText) && (!t.candidate.raceNo || m.raceNo === t.candidate.raceNo));
          return (
            <tr key={t._id}>
              <td className="raw">“{t.candidate.raw}”</td>
              <td><span className="pill">{reason(t.resolution.reason)}</span></td>
              <td>
                <select value={pick[t._id] || ''} onChange={e => setPick({ ...pick, [t._id]: e.target.value })}>
                  <option value="">Choose runner…</option>
                  {opts.slice(0, 12).map(m => (
                    <optgroup key={m.marketId} label={`${m.venue} R${m.raceNo} ${m.marketType} · ${time(m.startTime)}`}>
                      {m.runners.filter(r => r.status === 'ACTIVE').map(r => <option key={r.selectionId} value={`${m.marketId}|${r.selectionId}`}>{r.number}. {r.name} ({r.backPrice})</option>)}
                    </optgroup>))}
                </select>
              </td>
              <td className="nowrap"><select value={hu[t._id] || ''} onChange={e => setHu({ ...hu, [t._id]: Number(e.target.value) || undefined })} aria-label="Units"><option value="">As tipped</option>{UNIT_LADDER.map(u => <option key={u} value={u}>{ul(u)}</option>)}</select> <button className="sm" disabled={!pick[t._id]} onClick={() => confirm(t)}>Confirm</button> <button className="ghost sm" onClick={() => reject(t)}>Dismiss</button></td>
            </tr>);
        })}
      </tbody></table></div>
    </section>
  );
}

function Today({ rows, sources, onDone }) {
  const name = id => sources.find(s => s._id === id)?.name || '–';
  const place = async i => { try { await api(`/intents/${i._id}/place`, { method: 'POST' }); onDone(); } catch (e) { window.alert(msg(e)); } };
  return (
    <section className="card">
      <h2>Today</h2>
      {!rows.length ? <p className="fine">No bets yet today. Send some tips above.</p> :
        <div className="scroll"><table>
          <thead><tr><th>Jump</th><th>Race</th><th>Runner</th><th>Bet</th><th>Source</th><th className="r">Units</th><th className="r">Stake</th><th className="r">Tip price</th><th className="r">Taken</th><th>Status</th><th className="r">P/L</th></tr></thead>
          <tbody>{rows.map(i => (
            <tr key={i._id}>
              <td>{time(i.startTime)}</td><td>{i.venue} R{i.raceNo}</td><td>{i.runnerNumber}. {i.runnerName}</td>
              <td>{i.side === 'LAY' ? 'Lay ' : ''}{i.betType}</td><td className="muted">{name(i.sourceId)}</td>
              <td className="r">{i.units != null ? num(i.units, 1) + 'u' : ''}</td><td className="r">{money(i.stake)}</td><td className="r">{num(i.decisionPrice)}</td><td className="r">{i.fillPrice ? num(i.fillPrice) : (i.timing === 'BSP' ? 'BSP' : '–')}</td>
              <td>{i.status === 'ALERT_PENDING' ? <button className="sm" onClick={() => place(i)}>Place now</button> :
                <span className={'pill ' + (i.status === 'SKIPPED' || i.status === 'FAILED' ? 'muted' : '')} title={reason(i.skipReason)}>
                  {i.status === 'SETTLED' ? i.outcome : STATUS[i.status] || i.status}{i.skipReason && i.status !== 'ALERT_PENDING' ? ' · ' + reason(i.skipReason) : ''}</span>}
              </td>
              <td className={'r ' + (i.pnl > 0 ? 'pos' : i.pnl < 0 ? 'neg' : '')}>{i.status === 'SETTLED' ? money(i.pnl) : ''}</td>
            </tr>))}</tbody>
        </table></div>}
    </section>
  );
}

function BySource({ rows }) {
  return (
    <section className="card">
      <h2>Which sources make you money</h2>
      <div className="scroll"><table>
        <thead><tr><th>Source</th><th>Mode</th><th className="r">Bets</th><th className="r">Strike</th><th className="r">P/L</th><th className="r">Profit (u)</th><th className="r">POT</th><th className="r">Level POT</th><th className="r">Max DD (u)</th><th className="r">Worst run</th><th className="r">Price vs BSP</th></tr></thead>
        <tbody>{rows.map(({ source: s, metrics: m }) => (
          <tr key={s._id}>
            <td>{s.name}{s.provenance === 'CURATED' && <span className="pill muted">curated</span>}{s.kind === 'FOLLOW' && <span className="pill">mimic</span>}</td>
            <td>{s.active === false ? 'Paused' : s.mode}</td>
            <td className="r">{m.bets}</td><td className="r">{pct(m.strikeRate)}</td>
            <td className={'r ' + (m.pnl > 0 ? 'pos' : m.pnl < 0 ? 'neg' : '')}>{money(m.pnl)}</td>
            <td className="r">{num(m.profitUnits, 1)}</td><td className="r">{pct(m.pot)}</td><td className="r">{pct(m.levelPot)}</td>
            <td className="r">{num(m.maxDrawdownUnits, 1)}</td><td className="r">{m.longestLosingRun}</td><td className="r">{pct(m.priceEdgeVsBsp)}</td>
          </tr>))}</tbody>
      </table></div>
    </section>
  );
}

export default function Auto() {
  const { tick } = useApp();
  const [d, setD] = useState(null);
  const [k, setK] = useState(0);
  const reload = () => setK(x => x + 1);
  useEffect(() => { api('/dashboard').then(setD).catch(() => {}); }, [k, tick]);
  useEffect(() => { const t = setInterval(reload, 10000); return () => clearInterval(t); }, []);
  if (!d) return <p>Loading…</p>;
  const sources = d.bySource.map(b => b.source);
  const o = d.overall;
  return (
    <>
      <div className="stats">
        <div><b>{o.bets}</b><span>settled bets</span></div>
        <div><b>{pct(o.strikeRate)}</b><span>strike rate</span></div>
        <div><b className={o.pnl >= 0 ? 'pos' : 'neg'}>{money(o.pnl)}</b><span>P/L</span></div>
        <div><b className={o.profitUnits >= 0 ? 'pos' : 'neg'}>{num(o.profitUnits, 1)}u</b><span>profit (units)</span></div>
        <div><b>{pct(o.pot)}</b><span>POT</span></div>
        <div><b>{d.todays.filter(i => !['SKIPPED', 'FAILED'].includes(i.status)).length}</b><span>bets today</span></div>
      </div>
      <UnitBar />
      <Inbox sources={sources} onDone={reload} />
      <Held held={d.held} onDone={reload} />
      <Today rows={d.todays} sources={sources} onDone={reload} />
      <BySource rows={d.bySource} />
    </>
  );
}
